/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { IBlock } from '@datatype/type';
import * as color from '@constant/color';

interface IProps {
  design: Pick<IBlock['design'], 'box'>;
}

// <Pick<IProps['design'], 'box' | 'position' | 'button'>>
// const BoxBlock = styled.div<{ design: IProps['design']['box'] }>`
// const BoxBlock = styled.div<Pick<IProps, 'design'>>`
const BoxBlock = styled.div<{ design: Pick<IProps['design'], 'box'> }>`
  // flex
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props?.design?.box?.gap || '0px'};

  // shape
  box-sizing: border-box;
  min-width: 0;
  ${(props) => (props.design?.box?.width === 'fill' ? 'flex: 1;' : `width: ${props.design?.box?.width || 'auto'};`)}
  height: ${(props) => props?.design?.box?.height || 'auto'};
  padding: ${(props) => props?.design?.box?.padding || '0px'};
  margin: ${(props) => props?.design?.box?.margin || '0px'};
  max-width: ${(props) => props?.design?.box?.maxWidth || 'auto'};

  // background
  background-color: ${(props) => props?.design?.box?.backgroundColor || 'transparent'};

  // border
  ${(props) => {
    const boxDesign = props?.design?.box;
    const hasBorder =
      boxDesign?.border ||
      boxDesign?.shadow ||
      boxDesign?.borderWidth ||
      boxDesign?.borderColor ||
      boxDesign?.borderRadius;

    if (!hasBorder) {
      return css`
        border: none;
        border-radius: 0;
        box-shadow: none;
      `;
    }

    if (!boxDesign.borderDirections || boxDesign.borderDirections.length === 0) {
      return css`
        border: ${boxDesign.borderWidth || '0'} ${boxDesign.borderStyle || 'solid'}
          ${boxDesign.borderColor || color.BLACK_2};
        border-radius: ${boxDesign.borderRadius || '0'};
        box-shadow: ${boxDesign?.shadow || 'none'};
      `;
    }

    return css`
      border-top: ${boxDesign.borderDirections.includes('top')
        ? `${boxDesign.borderWidth || '0'} ${boxDesign.borderStyle || 'solid'} ${boxDesign.borderColor || color.BLACK_2}`
        : 'none'};
      border-right: ${boxDesign.borderDirections.includes('right')
        ? `${boxDesign.borderWidth || '0'} ${boxDesign.borderStyle || 'solid'} ${boxDesign.borderColor || color.BLACK_2}`
        : 'none'};
      border-bottom: ${boxDesign.borderDirections.includes('bottom')
        ? `${boxDesign.borderWidth || '0'} ${boxDesign.borderStyle || 'solid'} ${boxDesign.borderColor || color.BLACK_2}`
        : 'none'};
      border-left: ${boxDesign.borderDirections.includes('left')
        ? `${boxDesign.borderWidth || '0'} ${boxDesign.borderStyle || 'solid'} ${boxDesign.borderColor || color.BLACK_2}`
        : 'none'};
      border-radius: ${boxDesign?.borderRadius || '0px'};
      box-shadow: ${boxDesign?.shadow || 'none'};
    `;
    // return css`
    //   border: ${boxDesign?.borderWidth || '0'} solid ${boxDesign?.borderColor || color.BLACK_2};
    //   border-radius: ${boxDesign?.borderRadius || '0px'};
    //   box-shadow: ${boxDesign?.shadow || 'none'};
    // `;
  }}

  // align (box, text align) 나누기
  ${(props) => {
    const direction = props?.design?.box?.direction || 'row';
    const alignX = props?.design?.box?.alignX || 'center';
    const alignY = props?.design?.box?.alignY || 'center';

    const justify =
      direction === 'row'
        ? alignX === 'left'
          ? 'flex-start'
          : alignX === 'right'
            ? 'flex-end'
            : 'center'
        : alignY === 'top'
          ? 'flex-start'
          : alignY === 'bottom'
            ? 'flex-end'
            : 'center';

    const align =
      direction === 'row'
        ? alignY === 'top'
          ? 'flex-start'
          : alignY === 'bottom'
            ? 'flex-end'
            : 'center'
        : alignX === 'left'
          ? 'flex-start'
          : alignX === 'right'
            ? 'flex-end'
            : 'center';

    const textAlign = direction === 'row' ? alignX : 'center'; // column일 경우 대부분 center 처리

    return css`
      justify-content: ${justify};
      align-items: ${align};
      text-align: ${textAlign};
    `;
  }}

  // visibility
  visibility: ${(props) => props?.design?.box?.visibility || 'visible'};
  opacity: ${(props) => (props?.design?.box?.opacity !== undefined ? props.design.box.opacity : 1)};

  // position
  ${(props) => {
    const { top, bottom, left, right } = props?.design?.box || {};
    return (
      (top || bottom || left || right) &&
      css`
        position: absolute;
        ${top && `top: ${top};`}
        ${bottom && `bottom: ${bottom};`}
        ${left && `left: ${left};`}
        ${right && `right: ${right};`}
        transform: ${props?.design?.box?.transform || 'none'};
      `
    );
  }}

  transform: ${(props) => props?.design?.box?.transform || 'none'};

  // button (ImageTextBlock에서 덮어씌어지므로, 속성이 없으면 아무것도 설정안하게(부모설정 따라가게)
  ${(props) =>
    props?.design?.box?.event?.cursor &&
    css`
      cursor: ${props?.design?.box?.event?.cursor};
    `}

  ${(props) =>
    props?.design?.box?.event?.hover &&
    css`
      &:hover {
        ${props.design.box.event?.hover}
      }
    `}
`;

export default BoxBlock;
